import { createFileRoute, Link } from "@tanstack/react-router";
import { MetadataTab } from "../components/markadurinn/MetadataTab";
import { InstallationCostSection } from "../components/markadurinn/InstallationCostSection";
import { ServiceTermsSection } from "../components/markadurinn/ServiceTermsSection";
import { AtomikaShareSection } from "../components/markadurinn/AtomikaShareSection";
import { ExpectedSavingSection } from "../components/markadurinn/ExpectedSavingSection";
import { InvoiceHistorySection } from "../components/markadurinn/InvoiceHistorySection";
import { BuildingSitesSection } from "../components/markadurinn/BuildingSitesSection";
import { SkyrslurTab } from "../components/markadurinn/SkyrslurTab";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { api } from "../lib/api";
import { isAdmin, useAuth } from "../lib/auth";
import {
  CONTRACT_STATUS_META,
  DOCUMENT_KIND_LABELS,
  fmtBytes,
  fmtKr,
  fmtKrInput,
  fmtKrShort,
  fmtNum,
  ORG_META,
  parseKrInput,
  termsHaveBaseline,
  type Activity,
  type Contract,
  type ContractStatus,
  type CrmDocument,
  type DocumentKind,
  type CompanyBuilding,
  type CompanyDetail,
  type Contact,
  type CreateOpportunityPayload,
  type Customer,
  type Opportunity,
  type OrgId,
} from "../lib/markadur";

export const Route = createFileRoute(
  "/verkfaeri/markadurinn/fyrirtaeki/$kennitala",
)({
  component: CompanyPage,
});

type CompanyTab =
  | "yfirlit"
  | "tengilidir"
  | "samskipti"
  | "taekifaeri"
  | "samningar"
  | "hitaveitureikningar"
  | "skyrslur"
  | "skjol"
  | "metagogn"
  | "taeki";

const TABS: { id: CompanyTab; label: string }[] = [
  { id: "yfirlit", label: "Yfirlit" },
  { id: "tengilidir", label: "Tengiliðir" },
  { id: "samskipti", label: "Samskipti" },
  { id: "taekifaeri", label: "Tækifæri" },
  { id: "samningar", label: "Samningar" },
  { id: "hitaveitureikningar", label: "Hitaveitureikningar" },
  { id: "skyrslur", label: "Skýrslur" },
  { id: "skjol", label: "Skjöl" },
  { id: "metagogn", label: "Metagögn" },
  { id: "taeki", label: "Tæki og staðir" },
];

function CompanyPage() {
  const { kennitala } = Route.useParams();
  const { user } = useAuth();
  const salesOrg = (user?.salesOrg ?? "atomika") as OrgId;

  const [tab, setTab] = useState<CompanyTab>("yfirlit");
  const [detail, setDetail] = useState<CompanyDetail | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [termsRev, setTermsRev] = useState(0);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.getCompany(kennitala),
      api.getMarkadurCustomers().catch(() => ({ customers: [] as Customer[] })),
    ])
      .then(([d, cust]) => {
        setDetail(d);
        setCustomer(
          cust.customers.find((c) => c.kennitala === kennitala) ?? null,
        );
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setLoading(false));
  }, [kennitala]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (loading && !detail) {
    return (
      <div className="rounded-2xl border border-line bg-white px-5 py-16 text-center text-[14px] text-muted-2">
        Sæki fyrirtæki…
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <BackLink />
        <div className="rounded-2xl border border-danger-50 bg-danger-50 px-5 py-10 text-center text-[14px] font-semibold text-danger">
          {error || "Fyrirtæki fannst ekki."}
        </div>
      </div>
    );
  }

  const c = detail.company;
  const openOpps = detail.opportunities.filter((o) => o.status === "open");
  const wonOpps = detail.opportunities.filter((o) => o.status === "won");

  return (
    <div>
      <BackLink />

      <div className="mt-4 rounded-[18px] border border-line bg-white p-6 shadow-[0_8px_28px_rgba(11,36,71,.04)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="m-0 font-display text-[26px] font-extrabold tracking-tight text-ink">
              {c.name}
            </h1>
            <div className="mt-1 text-[13px] text-muted-2">
              {c.legalForm ? `${c.legalForm} · ` : ""}kt. {c.kennitala}
              {c.place ? ` · ${c.place}` : ""}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {customer?.activated ? (
              <span className="rounded-full bg-ok-50 px-2.5 py-1 text-[11px] font-bold text-ok">
                Virkur viðskiptavinur
              </span>
            ) : customer ? (
              <span className="rounded-full bg-canvas px-2.5 py-1 text-[11px] font-bold text-muted-2">
                Í bið
              </span>
            ) : null}
            {detail.reservation && (
              <span className="rounded-full bg-[#FEF3E4] px-2.5 py-1 text-[11px] font-bold text-[#A9701F]">
                Frátekið · {detail.reservation.daysRemaining}d
              </span>
            )}
            <Link
              to="/verkfaeri/atomika-reiknivel"
              search={reiknivelSearch(
                c.kennitala,
                detail.opportunities,
                detail.contracts,
              )}
              className="rounded-[11px] border border-line bg-white px-4 py-2 text-[13px] font-bold text-ink hover:bg-canvas"
            >
              Opna í reiknivél
            </Link>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="rounded-[11px] bg-[#F0871E] px-4 py-2 text-[13px] font-bold text-white hover:opacity-90"
            >
              + Tækifæri
            </button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-1.5 border-b border-line pb-0">
          {TABS.map((t) => {
            const count =
              t.id === "tengilidir"
                ? detail.contacts.length
                : t.id === "samskipti"
                  ? detail.activities.length
                  : t.id === "taekifaeri"
                    ? detail.opportunities.length
                    : t.id === "samningar"
                      ? detail.contracts.length
                      : t.id === "skjol"
                        ? detail.documents.length
                        : null;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`-mb-px border-b-2 px-3.5 py-2.5 text-[13px] font-bold transition ${
                  active
                    ? "border-[#F0871E] text-[#B5651A]"
                    : "border-transparent text-muted hover:text-ink"
                }`}
              >
                {t.label}
                {count != null ? (
                  <span className="ml-1.5 text-[11px] font-semibold text-muted-3">
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="pt-5">
          {tab === "yfirlit" && (
            <OverviewTab
              detail={detail}
              customer={customer}
              openOpps={openOpps}
              wonCount={wonOpps.length}
            />
          )}
          {tab === "tengilidir" && (
            <ContactsTab
              detail={detail}
              salesOrg={salesOrg}
              onChanged={reload}
            />
          )}
          {tab === "samskipti" && (
            <ActivitiesTab
              detail={detail}
              salesOrg={salesOrg}
              onChanged={reload}
            />
          )}
          {tab === "taekifaeri" && (
            <OpportunitiesTab
              kennitala={kennitala}
              opportunities={detail.opportunities}
              onCreate={() => setModalOpen(true)}
              canReopen={isAdmin(user)}
              onChanged={reload}
            />
          )}
          {tab === "samningar" && (
            <>
              <ServiceTermsSection
                buildings={detail.metadata.buildings}
                opportunities={detail.opportunities}
                onSaved={() => setTermsRev((n) => n + 1)}
              />
              <InstallationCostSection
                kennitala={kennitala}
                buildings={detail.metadata.buildings}
                onSaved={reload}
              />
              <ExpectedSavingSection kennitala={kennitala} />
              <AtomikaShareSection kennitala={kennitala} />
              <ContractsTab
                detail={detail}
                onChanged={reload}
                termsRev={termsRev}
                canArchiveSigned={isAdmin(user)}
              />
            </>
          )}
          {tab === "hitaveitureikningar" && (
            <InvoiceHistorySection kennitala={kennitala} />
          )}
          {tab === "skyrslur" && <SkyrslurTab kennitala={kennitala} />}
          {tab === "skjol" && (
            <DocumentsTab
              kennitala={kennitala}
              documents={detail.documents}
              onChanged={reload}
            />
          )}
          {tab === "metagogn" && (
            <MetadataTab
              kennitala={kennitala}
              metadata={detail.metadata}
              onSaved={reload}
            />
          )}
          {tab === "taeki" && (
            <ConnectionTab
              kennitala={kennitala}
              customer={customer}
              buildings={detail.metadata.buildings}
              onChanged={reload}
            />
          )}
        </div>
      </div>

      {modalOpen && (
        <QuickOppModal
          company={{ kennitala: c.kennitala, name: c.name }}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            reload();
          }}
        />
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/verkfaeri/markadurinn"
      search={{ tab: "search" }}
      className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-muted hover:text-ink"
    >
      ← Til baka í CRM
    </Link>
  );
}

function reiknivelSearch(
  kennitala: string,
  opportunities: Opportunity[],
  contracts: Contract[] = [],
): { kennitala: string; opportunityId?: number } {
  const taken = new Set(
    contracts.filter((c) => c.status !== "hafnad").map((c) => c.opportunityId),
  );
  const open = opportunities.filter((o) => o.status === "open");
  const pick = open.find((o) => !taken.has(o.id)) ?? open[0];
  return pick ? { kennitala, opportunityId: pick.id } : { kennitala };
}

function OverviewTab({
  detail,
  customer,
  openOpps,
  wonCount,
}: {
  detail: CompanyDetail;
  customer: Customer | null;
  openOpps: Opportunity[];
  wonCount: number;
}) {
  const c = detail.company;
  const lastActivity = detail.activities[0] ?? null;
  const [monthSaved, setMonthSaved] = useState<number | null>(null);
  const [yearSaved, setYearSaved] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    const now = new Date();
    const year = now.getFullYear();
    api
      .getSavings({
        from: `${year}-01-01`,
        to: now.toISOString().slice(0, 10),
        companyId: c.kennitala,
      })
      .then((d) => {
        if (!active) return;
        const daily = d.savings?.daily;
        if (!daily?.length) {
          setMonthSaved(null);
          setYearSaved(null);
          return;
        }
        let last = daily[0].date;
        for (const p of daily) if (p.date > last) last = p.date;
        const yearPrefix = `${last.slice(0, 4)}-`;
        const monthPrefix = `${last.slice(0, 7)}-`;
        let monthSum = 0;
        let yearSum = 0;
        for (const p of daily) {
          if (p.date.startsWith(yearPrefix)) yearSum += p.savedIsk;
          if (p.date.startsWith(monthPrefix)) monthSum += p.savedIsk;
        }
        setMonthSaved(monthSum);
        setYearSaved(yearSum);
      })
      .catch(() => {
        if (!active) return;
        setMonthSaved(null);
        setYearSaved(null);
      });
    return () => {
      active = false;
    };
  }, [c.kennitala]);

  return (
    <div className="space-y-5">
      {detail.reservation && (
        <div className="rounded-xl border border-[#F6D9AE] bg-[#FEF3E4] px-4 py-3">
          <div className="text-[12.5px] font-bold text-[#A9701F]">
            Árekstravörn · frátekið
          </div>
          <div className="mt-0.5 text-[12.5px] text-[#A9701F]">
            {detail.reservation.ownerUser || "Óskráður sölumaður"} ·{" "}
            {ORG_META[detail.reservation.ownerOrg].label} ·{" "}
            {detail.reservation.daysRemaining} dagar eftir · losnar{" "}
            {fmtDate(detail.reservation.expiresAt)}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Stat
          label="Sparnaður þennan mánuð"
          value={monthSaved == null ? "—" : fmtKr(monthSaved)}
        />
        <Stat
          label="Sparnaður á árinu"
          value={yearSaved == null ? "—" : fmtKr(yearSaved)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Opin tækifæri" value={String(openOpps.length)} />
        <Stat label="Unnin" value={String(wonCount)} />
        <Stat
          label="Unnið virði"
          value={customer ? fmtKrShort(customer.wonAmountKr) : "—"}
        />
        <Stat
          label="Síðasta snerting"
          value={lastActivity ? fmtDate(lastActivity.createdAt) : "—"}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Starfsfólk" value={fmtNum(c.staffCount)} />
        <Field label="Stærð" value={c.sizeClass || "—"} />
        <Field label="Landshluti" value={c.region || "—"} />
        <Field
          label="Rekstrartekjur"
          value={c.revenue ? fmtKr(c.revenue) : "—"}
        />
        <Field label="Sími" value={c.phone || "—"} />
        <Field label="Netfang" value={c.email || "—"} />
        <Field label="ISAT" value={c.isatLabel || "—"} />
      </div>

      {openOpps.length > 0 && (
        <div>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-muted-4">
            Opin tækifæri
          </div>
          <div className="space-y-1.5">
            {openOpps.map((o) => (
              <div
                key={o.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-line bg-canvas px-3.5 py-2.5"
              >
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-bold text-ink">
                    {o.title || o.companyName}
                  </div>
                  <div className="text-[11.5px] text-muted-2">
                    {fmtKrShort(o.amountKr)} · {o.probabilityPct}%
                  </div>
                </div>
                <Link
                  to="/verkfaeri/atomika-reiknivel"
                  search={{
                    kennitala: detail.company.kennitala,
                    opportunityId: o.id,
                  }}
                  className="shrink-0 rounded-lg bg-[#F0871E] px-3 py-1.5 text-[12px] font-bold text-white hover:opacity-90"
                >
                  Opna í reiknivél
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {detail.activities.length > 0 && (
        <div>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-muted-4">
            Nýleg samskipti
          </div>
          <div className="space-y-1.5">
            {detail.activities.slice(0, 5).map((a) => (
              <ActivityRow key={a.id} activity={a} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ContactsTab({
  detail,
  salesOrg,
  onChanged,
}: {
  detail: CompanyDetail;
  salesOrg: OrgId;
  onChanged: () => void;
}) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    api
      .createContact({
        companyKennitala: detail.company.kennitala,
        name,
        role: role || undefined,
        email: email || undefined,
        phone: phone || undefined,
        ownerOrg: salesOrg,
      })
      .then(() => {
        setName("");
        setRole("");
        setEmail("");
        setPhone("");
        onChanged();
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div className="space-y-4">
      {detail.contacts.length === 0 ? (
        <p className="text-[13px] text-muted-2">
          Engir tengiliðir skráðir enn.
        </p>
      ) : (
        <div className="space-y-2">
          {detail.contacts.map((contact) => (
            <ContactRow
              key={contact.id}
              contact={contact}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}

      <div className="rounded-xl border border-line p-4">
        <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.06em] text-muted-4">
          Bæta við tengilið
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nafn"
            className="rounded-lg border border-line px-3 py-2 text-[13px] outline-none focus:border-brand"
          />
          <input
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="Titill / hlutverk"
            className="rounded-lg border border-line px-3 py-2 text-[13px] outline-none focus:border-brand"
          />
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Sími"
            className="rounded-lg border border-line px-3 py-2 text-[13px] outline-none focus:border-brand"
          />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Netfang"
            className="rounded-lg border border-line px-3 py-2 text-[13px] outline-none focus:border-brand"
          />
        </div>
        {error && (
          <div className="mt-2 text-[12px] font-semibold text-danger">
            {error}
          </div>
        )}
        <button
          type="button"
          onClick={save}
          disabled={busy || !name.trim()}
          className="mt-3 rounded-lg bg-ink px-3.5 py-2 text-[12.5px] font-bold text-white disabled:opacity-50"
        >
          Vista tengilið
        </button>
      </div>
    </div>
  );
}

function ContactRow({
  contact,
  onChanged,
}: {
  contact: Contact;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = (p: Promise<unknown>) => {
    setBusy(true);
    setError(null);
    p.then(() => onChanged())
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div className="rounded-xl border border-line bg-canvas px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[14px] font-bold text-ink">
            {contact.name}
            {contact.role ? ` · ${contact.role}` : ""}
          </div>
          <div className="mt-0.5 text-[12.5px] text-muted-2">
            {[contact.phone, contact.email].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <button
          type="button"
          onClick={() => run(api.deleteContact(contact.id))}
          disabled={busy}
          className="flex-none text-[12px] font-semibold text-muted-3 hover:text-danger disabled:opacity-50"
        >
          Eyða
        </button>
      </div>
      <label className="mt-2 flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={contact.notifyOnAlerts}
          disabled={busy}
          onChange={() =>
            run(
              api.updateContact(contact.id, {
                notifyOnAlerts: !contact.notifyOnAlerts,
              }),
            )
          }
          className="h-4 w-4 accent-brand"
        />
        <span className="text-[12px] font-semibold text-ink">
          Fær frávikstilkynningar
        </span>
      </label>
      {error && (
        <div className="mt-1 text-[11.5px] font-semibold text-danger">
          {error}
        </div>
      )}
    </div>
  );
}

function ActivitiesTab({
  detail,
  salesOrg,
  onChanged,
}: {
  detail: CompanyDetail;
  salesOrg: OrgId;
  onChanged: () => void;
}) {
  const [type, setType] = useState<"call" | "email" | "meeting" | "note">(
    "call",
  );
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = () => {
    if (!note.trim()) return;
    setBusy(true);
    setError(null);
    api
      .createActivity({
        companyKennitala: detail.company.kennitala,
        type,
        note,
        actorOrg: salesOrg,
      })
      .then(() => {
        setNote("");
        onChanged();
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-line p-4">
        <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.06em] text-muted-4">
          Skrá samskipti
        </div>
        <div className="grid gap-2">
          <select
            value={type}
            onChange={(e) => setType(e.target.value as typeof type)}
            className="rounded-lg border border-line px-3 py-2 text-[13px] outline-none focus:border-brand"
          >
            <option value="call">Símtal</option>
            <option value="email">Tölvupóstur</option>
            <option value="meeting">Fundur</option>
            <option value="note">Minnispunktur</option>
          </select>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Niðurstaða samskipta"
            className="resize-none rounded-lg border border-line px-3 py-2 text-[13px] outline-none focus:border-brand"
          />
        </div>
        {error && (
          <div className="mt-2 text-[12px] font-semibold text-danger">
            {error}
          </div>
        )}
        <button
          type="button"
          onClick={save}
          disabled={busy || !note.trim()}
          className="mt-3 rounded-lg bg-ink px-3.5 py-2 text-[12.5px] font-bold text-white disabled:opacity-50"
        >
          Vista samskipti
        </button>
      </div>

      {detail.activities.length === 0 ? (
        <p className="text-[13px] text-muted-2">Engin samskipti skráð enn.</p>
      ) : (
        <div className="space-y-2">
          {detail.activities.map((a) => (
            <ActivityRow key={a.id} activity={a} />
          ))}
        </div>
      )}
    </div>
  );
}

function OpportunitiesTab({
  kennitala,
  opportunities,
  onCreate,
  canReopen,
  onChanged,
}: {
  kennitala: string;
  opportunities: Opportunity[];
  onCreate: () => void;
  canReopen: boolean;
  onChanged: () => void;
}) {
  // Reopening a won deal drops the company out of Viðskiptavinir — the customer list is
  // derived from won opportunities — and its dashboard goes with it. Admin only, and only
  // from here: the pipeline board no longer offers it at all.
  const reopen = (o: Opportunity) => {
    if (
      !window.confirm(
        `Opna „${o.title || o.companyName}" aftur?\n\n` +
          "Fyrirtækið hverfur þá úr Viðskiptavinum og mælaborðið þeirra — " +
          "sparnaður, gröf og frávik — sést ekki lengur í viðmótinu. " +
          "Gögnin sjálf eyðast ekki.",
      )
    ) {
      return;
    }
    void api.updateOpportunity(o.id, { status: "open" }).then(onChanged);
  };
  if (opportunities.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-[13px] text-muted-2">Engin tækifæri skráð.</p>
        <button
          type="button"
          onClick={onCreate}
          className="mt-3 rounded-lg bg-[#F0871E] px-4 py-2 text-[13px] font-bold text-white"
        >
          + Stofna tækifæri
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {opportunities.map((o) => (
        <div
          key={o.id}
          className="flex items-center justify-between rounded-xl border border-line bg-canvas px-4 py-3"
        >
          <div>
            <div className="flex items-center gap-2">
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: ORG_META[o.ownerOrg].color }}
              />
              <span className="text-[13px] font-bold text-ink">
                {o.title || ORG_META[o.ownerOrg].label}
              </span>
              {o.buildingName && o.buildingName !== o.title && (
                <span className="text-[11.5px] text-muted-2">
                  {o.buildingName}
                </span>
              )}
              <span className="text-[11.5px] text-muted-2">
                {o.status === "open"
                  ? `${o.probabilityPct}%`
                  : o.status === "won"
                    ? "Sala"
                    : "Tap"}
              </span>
            </div>
            {o.ownerUser && (
              <div className="mt-0.5 text-[12px] text-muted-2">
                {o.ownerUser}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {o.status === "open" && (
              <Link
                to="/verkfaeri/atomika-reiknivel"
                search={{ kennitala, opportunityId: o.id }}
                className="flex-none rounded-lg bg-[#F0871E] px-3 py-1.5 text-[12px] font-bold text-white hover:opacity-90"
              >
                Opna í reiknivél
              </Link>
            )}
            <div className="text-right font-display text-[14px] font-extrabold text-ink">
              {fmtKrShort(o.amountKr)}
            </div>
            {canReopen && o.status === "won" && (
              <button
                type="button"
                onClick={() => reopen(o)}
                title="Opna unnið tækifæri aftur — fyrirtækið hverfur úr Viðskiptavinum"
                className="flex-none rounded-lg border border-line bg-white px-2.5 py-1 text-[11.5px] font-bold text-muted-2 hover:bg-canvas"
              >
                Opna aftur
              </button>
            )}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={onCreate}
        className="mt-2 w-full rounded-lg border border-dashed border-line py-2.5 text-[13px] font-bold text-muted hover:bg-canvas"
      >
        + Stofna tækifæri
      </button>
    </div>
  );
}

function ConnectionTab({
  kennitala,
  customer,
  buildings,
  onChanged,
}: {
  kennitala: string;
  customer: Customer | null;
  buildings: CompanyBuilding[];
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setActive = (activate: boolean) => {
    setBusy(true);
    setError(null);
    const run = activate
      ? api.activateCustomer(kennitala)
      : api.deactivateCustomer(kennitala);
    run
      .then(onChanged)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-line bg-canvas p-4">
        {customer?.activated ? (
          <div className="flex items-center justify-between gap-3">
            <div className="text-[13px] text-muted-2">
              Virkur síðan {fmtDate(customer.activatedAt)}
              {customer.activatedBy ? ` · ${customer.activatedBy}` : ""}
            </div>
            <button
              type="button"
              onClick={() => setActive(false)}
              disabled={busy}
              className="rounded-lg border border-line bg-white px-3 py-1.5 text-[12px] font-semibold text-muted-2 disabled:opacity-50"
            >
              Afvirkja
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div className="text-[13px] text-muted-2">
              Virkjaðu viðskiptavininn til að tengja stjórnborð.
            </div>
            <button
              type="button"
              onClick={() => setActive(true)}
              disabled={busy}
              className="flex-none rounded-lg bg-ok px-3.5 py-1.5 text-[12.5px] font-bold text-white disabled:opacity-50"
            >
              {busy ? "Virkja…" : "Virkja viðskiptavin"}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-lg bg-danger-50 px-3 py-2 text-[12.5px] font-semibold text-danger">
          {error}
        </div>
      )}

      {customer?.activated && (
        <a
          href={`/verkfaeri/atomika-maelabord?customer=${encodeURIComponent(kennitala)}`}
          className="flex w-full items-center justify-center rounded-[12px] bg-ink px-4 py-[10px] text-[13px] font-bold text-white hover:opacity-90"
        >
          Fara í stjórnborð
          {customer.dashboardConnected ? " · tengt ✓" : " · bíð eftir gögnum"}
        </a>
      )}

      <BuildingSitesSection
        kennitala={kennitala}
        buildings={buildings}
        onSaved={onChanged}
      />
    </div>
  );
}

function QuickOppModal({
  company,
  onClose,
  onSaved,
}: {
  company: { kennitala: string; name: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const ownerOrg = user?.salesOrg ?? "atomika";
  const [ownerUser, setOwnerUser] = useState(user?.displayName ?? "");
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState(5_000_000);
  const [prob, setProb] = useState(30);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const weighted = Math.round(amount * (prob / 100));

  const save = () => {
    setSaving(true);
    setErr(null);
    api
      .createOpportunity({
        companyKennitala: company.kennitala,
        companyName: company.name,
        title: title || undefined,
        ownerOrg,
        ownerUser: ownerUser || undefined,
        amountKr: amount,
        probabilityPct: prob,
        notes: notes || undefined,
      } satisfies CreateOpportunityPayload)
      .then(onSaved)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setSaving(false));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-[480px] rounded-[20px] bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="m-0 font-display text-[18px] font-extrabold text-ink">
            Nýtt sölutækifæri
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-muted-2"
          >
            ✕
          </button>
        </div>
        <div className="mb-3 rounded-[12px] border border-line bg-canvas px-3.5 py-2.5 font-display text-[14px] font-bold text-ink">
          {company.name}
        </div>
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <Label>Sölumaður</Label>
            <input
              value={ownerUser}
              onChange={(e) => setOwnerUser(e.target.value)}
              className="w-full rounded-[12px] border border-line px-3.5 py-2.5 text-[14px] outline-none focus:border-brand"
            />
          </div>
          <div>
            <Label>Heiti</Label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Valfrjálst"
              className="w-full rounded-[12px] border border-line px-3.5 py-2.5 text-[14px] outline-none focus:border-brand"
            />
          </div>
        </div>
        <div className="mb-3">
          <Label>Upphæð (kr)</Label>
          <input
            type="text"
            inputMode="numeric"
            value={fmtKrInput(amount)}
            onChange={(e) => setAmount(parseKrInput(e.target.value))}
            className="w-full rounded-[12px] border border-line px-3.5 py-2.5 text-[14px] font-semibold tabular-nums outline-none focus:border-brand"
          />
        </div>
        <div className="mb-3">
          <Label>Líkur: {prob}%</Label>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={prob}
            onChange={(e) => setProb(Number(e.target.value))}
            className="w-full"
          />
        </div>
        <div className="mb-3">
          <Label>Athugasemd</Label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full resize-none rounded-[12px] border border-line px-3.5 py-2.5 text-[13px] outline-none focus:border-brand"
          />
        </div>
        <div className="mb-4 rounded-[12px] bg-[#FEF1E3] px-4 py-3">
          <div className="text-[11.5px] font-semibold text-[#A9701F]">
            Vigtað virði
          </div>
          <div className="font-display text-[20px] font-extrabold text-[#B5651A]">
            {fmtKr(weighted)}
          </div>
        </div>
        {err && (
          <div className="mb-3 text-[12.5px] font-semibold text-danger">
            {err}
          </div>
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-[12px] border border-line py-2.5 text-[13px] font-bold text-muted"
          >
            Hætta við
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex-1 rounded-[12px] bg-[#F0871E] py-2.5 text-[13px] font-bold text-white disabled:opacity-50"
          >
            {saving ? "Vista…" : "Stofna tækifæri"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActivityRow({ activity }: { activity: Activity }) {
  const typeLabel: Record<string, string> = {
    call: "Símtal",
    email: "Póstur",
    meeting: "Fundur",
    note: "Minnispunktur",
  };
  return (
    <div className="rounded-xl border border-line bg-canvas px-4 py-3">
      <div className="flex justify-between gap-2 text-[11px] font-semibold text-muted-3">
        <span>
          {typeLabel[activity.type] || activity.type} ·{" "}
          {activity.actorUser ||
            ORG_META[activity.actorOrg as OrgId]?.label ||
            activity.actorOrg}
        </span>
        <span>{fmtDate(activity.createdAt)}</span>
      </div>
      <div className="mt-1 text-[13px] text-ink">{activity.note}</div>
    </div>
  );
}

function ContractsTab({
  detail,
  onChanged,
  termsRev,
  canArchiveSigned,
}: {
  detail: CompanyDetail;
  onChanged: () => void;
  termsRev: number;
  canArchiveSigned: boolean;
}) {
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasBaseline, setHasBaseline] = useState<boolean | null>(null);
  const buildingKey = detail.metadata.buildings.map((b) => b.id).join(",");

  useEffect(() => {
    const buildings = detail.metadata.buildings;
    if (buildings.length === 0) {
      setHasBaseline(false);
      return;
    }
    let alive = true;
    Promise.all(buildings.map((b) => api.listServiceTerms(b.id)))
      .then((lists) => {
        if (!alive) return;
        setHasBaseline(lists.some((l) => l.versions.some(termsHaveBaseline)));
      })
      .catch(() => {
        if (alive) setHasBaseline(false);
      });
    return () => {
      alive = false;
    };
  }, [buildingKey, detail.metadata.buildings, termsRev]);

  const contractedOppIds = new Set(
    detail.contracts
      .filter((c) => c.status !== "hafnad")
      .map((c) => c.opportunityId),
  );
  const ready = detail.opportunities.filter(
    (o) =>
      (o.status === "open" || o.status === "won") &&
      !contractedOppIds.has(o.id),
  );

  const create = (opportunityId: number) => {
    setBusyId(opportunityId);
    setError(null);
    api
      .createContract({ opportunityId })
      .then(onChanged)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setBusyId(null));
  };

  const setStatus = (id: number, status: ContractStatus) => {
    setBusyId(id);
    setError(null);
    api
      .updateContract(id, { status })
      .then(onChanged)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setBusyId(null));
  };

  const regenerate = (id: number) => {
    setBusyId(id);
    setError(null);
    api
      .regenerateContract(id)
      .then(onChanged)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setBusyId(null));
  };

  const remove = (id: number, signed: boolean) => {
    let reason: string | undefined;
    if (signed) {
      const answer = window.prompt(
        "Af hverju er samningurinn tekinn úr gildi? Samningurinn og undirritaða skjalið eru geymd áfram.",
      );
      if (answer == null || answer.trim() === "") return;
      reason = answer.trim();
    }
    setBusyId(id);
    setError(null);
    api
      .deleteContract(id, reason)
      .then(onChanged)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setBusyId(null));
  };

  const empty = detail.contracts.length === 0 && ready.length === 0;

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-xl bg-danger-50 px-3.5 py-2.5 text-[12.5px] font-semibold text-danger">
          {error}
        </div>
      )}

      {empty && (
        <div className="py-8 text-center text-[13px] text-muted-2">
          Engir samningar. Fylltu þjónustusamninginn hér að ofan — mánaðarlegt
          rúmmál, þóknun og greiðslu búnaðar — og stofnaðu svo samning af
          tækifæri.
        </div>
      )}

      {detail.contracts.map((c) => (
        <ContractCard
          key={c.id}
          contract={c}
          busy={busyId === c.id}
          onStatus={(status) => setStatus(c.id, status)}
          onRegenerate={
            c.status === "drog" && c.signedAt == null
              ? () => regenerate(c.id)
              : undefined
          }
          onDelete={
            c.signedAt == null
              ? () => remove(c.id, false)
              : canArchiveSigned
                ? () => remove(c.id, true)
                : undefined
          }
          deleteLabel={c.signedAt == null ? "Eyða" : "Taka úr gildi"}
        />
      ))}

      {ready.length > 0 && (
        <div className="rounded-xl border border-dashed border-line p-4">
          <Label>Tilbúið til samningsgerðar</Label>
          {hasBaseline === false && (
            <p className="mb-3 text-[12.5px] font-semibold text-[#A9701F]">
              Ekki hægt að stofna samning án grunnlínu. Sækjaðu eða sláðu inn
              mánaðarlegt rúmmál í þjónustusamningi fyrst.
            </p>
          )}
          <div className="space-y-2">
            {ready.map((o) => (
              <div
                key={o.id}
                className="flex items-center justify-between gap-3 rounded-lg bg-canvas px-3.5 py-2.5"
              >
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-bold text-ink">
                    {o.title}
                  </div>
                  <div className="text-[11.5px] text-muted-2">
                    {fmtKrShort(o.amountKr)}
                    {o.scenarioId != null ? ` · áætlun #${o.scenarioId}` : ""}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Link
                    to="/verkfaeri/atomika-reiknivel"
                    search={{
                      kennitala: detail.company.kennitala,
                      opportunityId: o.id,
                    }}
                    className="rounded-lg border border-line bg-white px-3 py-2 text-[12.5px] font-bold text-ink"
                  >
                    Opna í reiknivél
                  </Link>
                  <button
                    type="button"
                    onClick={() => create(o.id)}
                    disabled={busyId === o.id || hasBaseline !== true}
                    title={
                      hasBaseline === true
                        ? undefined
                        : "Ekki hægt að stofna samning án grunnlínu."
                    }
                    className="rounded-lg bg-[#F0871E] px-3.5 py-2 text-[12.5px] font-bold text-white disabled:opacity-50"
                  >
                    {busyId === o.id ? "Stofna…" : "Stofna samning"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ContractCard({
  contract,
  busy,
  onStatus,
  onRegenerate,
  onDelete,
  deleteLabel,
}: {
  contract: Contract;
  busy: boolean;
  onStatus: (status: ContractStatus) => void;
  onRegenerate?: () => void;
  onDelete?: () => void;
  deleteLabel: string;
}) {
  const meta = CONTRACT_STATUS_META[contract.status];
  const t = contract.terms;

  return (
    <div className="rounded-xl border border-line bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display text-[15px] font-extrabold text-ink">
              {contract.contractNumber}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${meta.className}`}
            >
              {meta.label}
            </span>
          </div>
          <div className="mt-0.5 truncate text-[12.5px] text-muted-2">
            {contract.title}
          </div>
          {contract.templateVersion && (
            <div className="mt-0.5 text-[11.5px] text-muted-2">
              Samningsform {contract.templateVersion}
              {contract.serviceTermsId != null
                ? ` · þjónustusamningur #${contract.serviceTermsId}`
                : ""}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={contract.status}
            disabled={busy}
            onChange={(e) => onStatus(e.target.value as ContractStatus)}
            className="rounded-lg border border-line bg-white px-2.5 py-1.5 text-[12px] font-semibold text-ink disabled:opacity-50"
          >
            {(Object.keys(CONTRACT_STATUS_META) as ContractStatus[]).map(
              (s) => (
                <option key={s} value={s}>
                  {CONTRACT_STATUS_META[s].label}
                </option>
              ),
            )}
          </select>
          {contract.documentId != null && (
            <>
              <OpenDocumentButton
                documentId={contract.documentId}
                label="Opna samning"
              />
              <OpenDocumentButton
                documentId={contract.documentId}
                label="Sækja PDF"
                autoPrint
              />
            </>
          )}
          {onRegenerate && (
            <button
              type="button"
              onClick={onRegenerate}
              disabled={busy}
              title="Byggir skjalið upp á nýtt úr gildandi gögnum og sniðmáti."
              className="rounded-lg border border-line bg-white px-3 py-1.5 text-[12px] font-bold text-muted hover:bg-canvas disabled:opacity-50"
            >
              Endurgera skjal
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              className="rounded-lg px-3 py-1.5 text-[12px] font-bold text-danger hover:bg-danger-50 disabled:opacity-50"
            >
              {deleteLabel}
            </button>
          )}
        </div>
      </div>

      <div className="mt-3.5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {t.monthlyFeeKr != null ? (
          <Field label="Mánaðargjald" value={fmtKrShort(t.monthlyFeeKr)} />
        ) : (
          <>
            <Field label="Lækkun" value={`${t.savingsPct}%`} />
            <Field
              label="Skipting"
              value={`${t.customerSharePct}/${100 - t.customerSharePct}`}
            />
          </>
        )}
        <Field
          label="Búnaður"
          value={
            t.paymentOption === "fyrirfram"
              ? "Viðskiptavinur kaupir"
              : "Greitt með sparnaði"
          }
        />
        <Field label="Samningstími" value={`${t.horizonYears} ár`} />
        <Field label="Uppsetning" value={fmtKrShort(t.systemCostKr)} />
        <Field label="Gildir frá" value={fmtDate(contract.startDate)} />
        <Field label="Undirritað" value={fmtDate(contract.signedAt)} />
      </div>
    </div>
  );
}

const PRINT_HOOK =
  '<script>addEventListener("load",()=>window.print())</script>';

async function printable(blob: Blob): Promise<Blob> {
  const html = await blob.text();
  return new Blob([html + PRINT_HOOK], { type: "text/html;charset=utf-8" });
}

function OpenDocumentButton({
  documentId,
  label,
  autoPrint,
}: {
  documentId: number;
  label: string;
  autoPrint?: boolean;
}) {
  const [busy, setBusy] = useState(false);

  const open = () => {
    setBusy(true);
    // The tab has to be opened inside the click gesture — a window.open() deferred until
    // after the authenticated fetch resolves is treated as a popup and blocked.
    const tab = window.open("", "_blank");
    api
      .fetchDocument(documentId)
      .then((blob) => (autoPrint ? printable(blob) : blob))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        if (tab) {
          tab.location.href = url;
        } else {
          window.location.href = url;
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch(() => tab?.close())
      .finally(() => setBusy(false));
  };

  return (
    <button
      type="button"
      onClick={open}
      disabled={busy}
      className="rounded-lg border border-line bg-white px-3 py-1.5 text-[12px] font-bold text-muted hover:bg-canvas disabled:opacity-50"
    >
      {busy ? "Sæki…" : label}
    </button>
  );
}

function DocumentsTab({
  kennitala,
  documents,
  onChanged,
}: {
  kennitala: string;
  documents: CrmDocument[];
  onChanged: () => void;
}) {
  const [kind, setKind] = useState<DocumentKind>("tilbod");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = (file: File) => {
    setBusy(true);
    setError(null);
    api
      .uploadDocument({ file, kennitala, kind })
      .then(onChanged)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setBusy(false));
  };

  const remove = (id: number) => {
    setBusy(true);
    setError(null);
    api
      .deleteDocument(id)
      .then(onChanged)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-xl bg-danger-50 px-3.5 py-2.5 text-[12.5px] font-semibold text-danger">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-line bg-canvas p-4">
        <Label>Hlaða upp skjali</Label>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as DocumentKind)}
            className="rounded-lg border border-line bg-white px-3 py-2 text-[13px] font-semibold text-ink"
          >
            {(Object.keys(DOCUMENT_KIND_LABELS) as DocumentKind[]).map((k) => (
              <option key={k} value={k}>
                {DOCUMENT_KIND_LABELS[k]}
              </option>
            ))}
          </select>
          <label className="cursor-pointer rounded-lg bg-[#F0871E] px-4 py-2 text-[13px] font-bold text-white hover:opacity-90">
            {busy ? "Hleð upp…" : "Velja skrá"}
            <input
              type="file"
              className="hidden"
              disabled={busy}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) upload(file);
              }}
            />
          </label>
          <span className="text-[11.5px] text-muted-3">
            PDF, Word, Excel eða mynd · hámark 15 MB
          </span>
        </div>
      </div>

      {documents.length === 0 ? (
        <div className="py-8 text-center text-[13px] text-muted-2">
          Engin skjöl skráð á fyrirtækið.
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map((d) => (
            <div
              key={d.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-line bg-canvas px-4 py-3"
            >
              <div className="min-w-0">
                <div className="truncate text-[13px] font-bold text-ink">
                  {d.fileName}
                </div>
                <div className="mt-0.5 text-[11.5px] text-muted-2">
                  {d.kindLabel} · {fmtBytes(d.sizeBytes)} ·{" "}
                  {fmtDate(d.createdAt)}
                  {d.uploadedBy ? ` · ${d.uploadedBy}` : ""}
                  {d.source === "generated" ? " · útgefið af Atomika" : ""}
                  {d.contentSha256 ? ` · ${d.contentSha256.slice(0, 12)}` : ""}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <OpenDocumentButton documentId={d.id} label="Opna" />
                {d.source === "upload" && (
                  <button
                    type="button"
                    onClick={() => remove(d.id)}
                    disabled={busy}
                    className="rounded-lg border border-line bg-white px-3 py-1.5 text-[12px] font-semibold text-muted-2 hover:text-danger disabled:opacity-50"
                  >
                    Eyða
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-line bg-canvas px-3.5 py-3">
      <div className="text-[11px] font-semibold text-muted-3">{label}</div>
      <div className="mt-0.5 font-display text-[18px] font-extrabold text-ink">
        {value}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold text-muted-3">{label}</div>
      <div className="text-[13px] font-semibold text-ink">{value}</div>
    </div>
  );
}

function Label({ children }: { children: ReactNode }) {
  return (
    <div className="mb-1 text-[11.5px] font-bold uppercase tracking-[0.05em] text-muted-4">
      {children}
    </div>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("is-IS");
}
